// src/components/groupqm/GroupQmSurface.tsx — Sign-In System (Build 2) Home
// Group QuickMessage surface (heather lane). Prototype State 6, reshaped per
// Chris (2026-05-29): a single "Group Messages" strip listing the groups you're
// in, each row showing whether it's active (members on duty) and any unread,
// tap a row to open its thread. No compose button here — you post from inside a
// group's thread.
//
// Gating: feature on AND you're sign-in eligible (kickoff §1's Home-surface
// gate). Deliberately NOT gated on being on duty *right now* — that would make
// your groups vanish the moment you sign off, which defeats the "always be able
// to get back to a thread" goal. Posting is still duty-gated inside the thread.
//
// Heather #7A5D8C only — never the royal-blue 1:1 Quick Message lane.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../api";
import type { MeResponse } from "../../auth";
import { G, GF, GRP_KEYFRAMES, truncate, initialsFromName } from "./groupTokens";

// ---------------------------------------------------------------------------
// Wire types (subset of GET /api/group-qm/me we use here)
// ---------------------------------------------------------------------------

interface ActiveSegmentBanner {
  id: number;
  unreadCount: number;
  lastMessageAt: string | null;
  snippet: { senderFirstName: string; body: string };
}
interface MyGroup {
  id: number;
  code: string;
  displayName: string;
  memberCount: number;
  signedInMemberCount: number;
  activeSegment: ActiveSegmentBanner | null;
}

// ---------------------------------------------------------------------------
// Surface
// ---------------------------------------------------------------------------

export function GroupQmSurface({ me }: { me: MeResponse | null }) {
  // Visible whenever you're logged in, the feature is on, and you're in at least
  // one group — deliberately NOT tied to sign-in eligibility or being on duty
  // (Chris: the strip must stay up the whole time you're logged in). Gating on
  // me.signIn.eligible was wrong twice over: group membership (not duty
  // eligibility) is what should decide it, AND /api/me's sign-in block falls
  // back to ineligible on any transient read failure, which made the strip
  // flicker away. me.features defaults ON in that same fallback, so the
  // feature-flag gate fails open. Reading a thread needs only membership;
  // posting stays gated on an open duty session inside the thread.
  const featureOff = me?.features?.groupQuickMessage === false;
  const navigate = useNavigate();
  const [groups, setGroups] = useState<MyGroup[] | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch<{ groups: MyGroup[] }>("/api/group-qm/me");
      setGroups(r.groups);
    } catch {
      // Keep whatever we had rather than blanking the strip on a transient
      // failure; only an empty success (handled below) hides it.
      setGroups((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    if (featureOff) return;
    void load();
  }, [featureOff, load]);

  // Nothing to show unless the feature is on and you're in at least one group.
  if (featureOff || !groups || groups.length === 0) return null;

  // Sort: unread first, then active (members on duty), then the rest.
  const sorted = [...groups].sort((a, b) => {
    const au = a.activeSegment?.unreadCount ?? 0;
    const bu = b.activeSegment?.unreadCount ?? 0;
    if ((bu > 0 ? 1 : 0) !== (au > 0 ? 1 : 0)) return bu - au > 0 ? 1 : -1;
    if (bu !== au) return bu - au;
    return b.signedInMemberCount - a.signedInMemberCount;
  });

  return (
    <div>
      <style>{GRP_KEYFRAMES}</style>
      <div
        style={{
          background: G.card,
          border: `1px solid ${G.grpLt}`,
          borderRadius: 16,
          boxShadow: "0 1px 0 rgba(122,93,140,0.06), 0 4px 12px rgba(122,93,140,0.10)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg, ${G.grp}, ${G.grpDk})` }} />
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "12px 14px 8px 16px",
          fontFamily: GF.body, fontSize: 9.5, fontWeight: 800, letterSpacing: "1.4px",
          textTransform: "uppercase", color: G.grpDk,
        }}>
          <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: "50%", background: G.grp }} />
          Group Messages
        </div>

        <div style={{ padding: "0 6px 6px" }}>
          {sorted.map((g) => (
            <GroupRow key={g.id} group={g} onOpen={() => navigate(`/group-qm/${g.id}`)} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row — one group: avatar, name, active/unread state, tap to open
// ---------------------------------------------------------------------------

function GroupRow({ group, onOpen }: { group: MyGroup; onOpen: () => void }) {
  const seg = group.activeSegment;
  const unread = seg?.unreadCount ?? 0;
  const onDuty = group.signedInMemberCount;
  const initials = initialsFromName(group.displayName, group.code);

  // Meta line: latest unread snippet > on-duty presence > member count.
  let meta: React.ReactNode;
  if (unread > 0 && seg) {
    meta = (
      <span style={{ fontFamily: GF.serif, fontStyle: "italic", color: G.inkSoft }}>
        {seg.snippet.senderFirstName} · "{truncate(seg.snippet.body, 38)}"
      </span>
    );
  } else if (onDuty > 0) {
    meta = (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: G.grpDk, fontWeight: 700 }}>
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "#4F6852" }} />
        Active · {onDuty} on duty
      </span>
    );
  } else {
    meta = <span style={{ color: G.inkMute }}>{group.memberCount} {group.memberCount === 1 ? "member" : "members"}</span>;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${group.displayName}${unread > 0 ? `, ${unread} new` : ""}`}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
        padding: "10px 10px", background: "transparent", border: "none", borderRadius: 12, cursor: "pointer",
      }}
    >
      <span style={{
        width: 38, height: 38, borderRadius: 12, background: `linear-gradient(135deg, ${G.grpLt}, ${G.grp})`,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: GF.serif, fontWeight: 700, fontSize: 12.5, flexShrink: 0, letterSpacing: "0.4px",
        position: "relative",
        boxShadow: unread > 0 ? "inset 0 1px 0 rgba(255,255,255,0.22)" : "inset 0 1px 0 rgba(255,255,255,0.18)",
      }}>
        {initials}
        {unread > 0 && (
          <span aria-hidden="true" style={{ position: "absolute", top: -3, right: -3, width: 9, height: 9, borderRadius: "50%", background: G.grp, animation: "grpPulse 1.8s ease-out infinite", border: `2px solid ${G.card}` }} />
        )}
      </span>

      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontFamily: GF.serif, fontSize: 15, fontWeight: 700, color: G.ink, letterSpacing: "-0.2px", lineHeight: 1.15 }}>
          {group.displayName}
        </span>
        <span style={{ display: "block", fontFamily: GF.body, fontSize: 12, marginTop: 2, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {meta}
        </span>
      </span>

      {unread > 0 ? (
        <span style={{
          background: G.grpDk, color: "#fff", fontFamily: GF.body, fontSize: 11, fontWeight: 800,
          minWidth: 20, height: 20, borderRadius: 10, padding: "0 6px", flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {unread > 9 ? "9+" : unread}
        </span>
      ) : (
        <span style={{ color: G.grpDk, fontSize: 18, fontWeight: 700, flexShrink: 0 }}>›</span>
      )}
    </button>
  );
}
